import { useState, useEffect } from "react";
import { db } from "./firebase";
import {
  collection,
  query,
  where,
  getDocs
} from "firebase/firestore";
import LoginPage from "./LoginPage";

function App() {

  const [memberId, setMemberId] = useState("");
  const [password, setPassword] = useState("");
  const [memberData, setMemberData] = useState(null);
  const [error, setError] = useState("");
  const [activePage, setActivePage] = useState("dashboard");

  const [transactions, setTransactions] = useState([]);
  const [balance, setBalance] = useState(0);

  /* ================= LOGIN ================= */

  const handleLogin = async () => {

    if (!memberId || !password) {
      setError("Please enter Member ID and Password");
      return;
    }

    const q = query(
      collection(db, "members"),
      where("memberId", "==", memberId.trim()),
      where("password", "==", password.trim())
    );

    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      setMemberData(snapshot.docs[0].data());
      setError("");
    } else {
      setError("Invalid Member ID or Password ❌");
    }
  };

  const handleLogout = () => {
    setMemberData(null);
    setMemberId("");
    setPassword("");
    setActivePage("dashboard");
  };

  /* ================= LOAD LEDGER ================= */

  useEffect(() => {
    if (memberData) loadLedger();
  }, [memberData]);

  const loadLedger = async () => {

    const q = query(
      collection(db, "savingLedger"),
      where("memberId", "==", memberData.memberId)
    );

    const snapshot = await getDocs(q);

    let txns = snapshot.docs.map(doc => doc.data());

    // Sort oldest first for correct balance calculation
    txns.sort((a, b) =>
      (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0)
    );

    let running = 0;

    const calculated = txns.map(t => {
      running += Number(t.deposit || 0);
      running -= Number(t.withdrawal || 0);
      return { ...t, runningBalance: running };
    });

    setBalance(running);

    // Show latest on top
    setTransactions(calculated.reverse());
  };

  /* ================= UI ================= */

  if (memberData) {
    return (
      <div style={styles.wrapper}>

        <div style={styles.header}>
          <h2>SMB MEMBERS</h2>
          <button style={styles.logoutBtn} onClick={handleLogout}>
            Logout
          </button>
        </div>

        <div style={styles.main}>

          <div style={styles.sidebar}>
            <p style={styles.menu} onClick={() => setActivePage("dashboard")}>Dashboard</p>
            <p style={styles.menu} onClick={() => setActivePage("ledger")}>Ledger</p>
            <p style={styles.menu} onClick={() => setActivePage("transfer")}>Fund Transfer</p>
            <p style={styles.menu} onClick={() => setActivePage("profile")}>Profile</p>
            <p style={styles.menu} onClick={() => setActivePage("support")}>Support</p>
          </div>

          <div style={styles.content}>

            {activePage === "dashboard" && (
              <>
                <h2 style={{ color: "#8b1245" }}>
                  Welcome {memberData.name}
                </h2>

                <div style={styles.balanceCard}>
                  <p>Available Balance</p>
                  <h1>₹ {balance.toLocaleString()}</h1>
                </div>
              </>
            )}

            {activePage === "ledger" && (
              <>
                <h2 style={{ marginBottom: "20px" }}>Ledger Statement</h2>

                <div style={{ overflowX: "auto" }}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Sr</th>
                        <th style={styles.th}>Date & Time</th>
                        <th style={styles.th}>Particular</th>
                        <th style={styles.th}>Withdrawal</th>
                        <th style={styles.th}>Deposit</th>
                        <th style={styles.th}>Balance</th>
                      </tr>
                    </thead>

                    <tbody>
                      {transactions.map((t, i) => (
                        <tr key={i}>
                          <td style={styles.td}>{i + 1}</td>

                          <td style={styles.td}>
                            {t.createdAt?.seconds
                              ? new Date(
                                  t.createdAt.seconds * 1000
                                ).toLocaleString()
                              : "-"}
                          </td>

                          <td style={styles.td}>
                            {t.particular || "Transaction"}
                          </td>

                          <td style={{ ...styles.td, color: "red", textAlign: "right" }}>
                            ₹ {Number(t.withdrawal || 0).toLocaleString()}
                          </td>

                          <td style={{ ...styles.td, color: "green", textAlign: "right" }}>
                            ₹ {Number(t.deposit || 0).toLocaleString()}
                          </td>

                          <td style={{ ...styles.td, fontWeight: "bold", textAlign: "right" }}>
                            ₹ {Number(t.runningBalance).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {activePage === "transfer" && (
              <>
                <h2>Fund Transfer</h2>
                <div style={styles.comingSoon}>🚀 Coming Soon</div>
              </>
            )}

            {activePage === "profile" && (
              <div style={styles.infoCard}>
                <p><b>Name:</b> {memberData.name}</p>
                <p><b>Member ID:</b> {memberData.memberId}</p>
                <p><b>Mobile:</b> {memberData.mobile}</p>
              </div>
            )}

            {activePage === "support" && (
              <p>Contact: support@smb.com</p>
            )}

          </div>
        </div>
      </div>
    );
  }

  return (
    <LoginPage
      memberId={memberId}
      setMemberId={setMemberId}
      password={password}
      setPassword={setPassword}
      handleLogin={handleLogin}
      error={error}
    />
  );
}

/* ================= STYLES ================= */

const styles = {

  wrapper: { fontFamily: "Arial", background: "#f8f5f7", minHeight: "100vh" },

  header: {
    background: "linear-gradient(90deg, #8b1245, #5e0d2e)",
    color: "white",
    padding: "15px 30px",
    display: "flex",
    justifyContent: "space-between"
  },

  logoutBtn: {
    background: "white",
    color: "#8b1245",
    border: "none",
    padding: "8px 16px",
    borderRadius: "6px",
    cursor: "pointer"
  },

  main: { display: "flex" },

  sidebar: {
    width: "220px",
    background: "#5e0d2e",
    color: "white",
    padding: "20px",
    minHeight: "90vh"
  },

  menu: { padding: "12px", cursor: "pointer" },

  content: { flex: 1, padding: "30px" },

  balanceCard: {
    background: "linear-gradient(90deg, #8b1245, #5e0d2e)",
    color: "white",
    padding: "30px",
    borderRadius: "12px",
    width: "350px"
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    background: "white"
  },

  th: {
    border: "1px solid #ccc",
    padding: "10px",
    background: "#1f1f1f",
    color: "white",
    textAlign: "left"
  },

  td: {
    border: "1px solid #ccc",
    padding: "10px"
  },

  comingSoon: {
    marginTop: "20px",
    padding: "30px",
    background: "#fff3cd",
    borderRadius: "10px",
    fontSize: "22px",
    fontWeight: "bold"
  },

  infoCard: {
    background: "white",
    padding: "20px",
    borderRadius: "12px",
    boxShadow: "0 3px 10px rgba(0,0,0,0.1)"
  }
};

export default App;